import { ImpulseElement, property, registerElement, target, targets } from '@ambiki/impulse';
import type AwcDialogElement from '@ambiki/impulse-view-components/dist/elements/dialog';
import { createHiddenInput } from 'helpers/form';

@registerElement('patient-attendance')
export default class PatientAttendanceElement extends ImpulseElement {
  /**
   * Whether the element is active or not. If active, then attendance can be marked.
   */
  @property({ type: Boolean }) active = false;

  /**
   * An array of patient ids that were selected from the checkboxes.
   */
  @property({ type: Array }) selectedPatientIds: string[] = [];

  /**
   * The form name of the attendance type.
   */
  @property() attendanceTypeIdName: string;

  /**
   * The form name of the patient ids.
   */
  @property() patientIdsName: string;

  /**
   * JSON for all attendance types.
   */
  @property({ type: Array }) attendanceTypes: Array<{ id: string; name: string }>;

  /**
   * DOM ID for the attendance dialog.
   */
  @property() randomId: string;

  @target() dialog: AwcDialogElement;
  @target() dropdown: HTMLElement;
  @target() button: HTMLButtonElement;
  @target() form: HTMLFormElement;
  @target() presentForm?: HTMLFormElement;
  @target() clearForm: HTMLFormElement;
  @target() sameReasonContainer: HTMLElement;
  @target() sameReasonTextareaContainer: HTMLElement;
  @targets() textareaContainers: HTMLElement[];

  constructor() {
    super();
    this.handleDropdownShown = this.handleDropdownShown.bind(this);
    this.handleDropdownHidden = this.handleDropdownHidden.bind(this);
  }

  /**
   * Called when the element is added to the DOM.
   */
  connected() {
    $(this.dropdown).on('shown.bs.dropdown', this.handleDropdownShown);
    $(this.dropdown).on('hidden.bs.dropdown', this.handleDropdownHidden);
    if (this.active) {
      this.button.disabled = false;
    }
  }

  /**
   * Called when the element is removed from the DOM.
   */
  disconnected() {
    $(this.dropdown).off('shown.bs.dropdown', this.handleDropdownShown);
    $(this.dropdown).off('hidden.bs.dropdown', this.handleDropdownHidden);
  }

  /**
   * Called when the `active` property changes it's state.
   */
  activeChanged(active: boolean) {
    this.button.disabled = !active;
  }

  /**
   * Called when the dialog is shown. This is a great place to initialize things.
   */
  dialogShown(event: CustomEvent<{ dialog: AwcDialogElement; relatedTarget: HTMLButtonElement }>) {
    const invoker = event.detail.relatedTarget;
    const attendanceTypeId = invoker.getAttribute('data-attendance-type-id');
    if (!attendanceTypeId) return;

    this.updateDialogTitle(attendanceTypeId);
    const hiddenInput = createHiddenInput(this.attendanceTypeIdName, attendanceTypeId);
    this.form.append(hiddenInput);
    // Hide if there is only patient that is selected.
    this.sameReasonContainer.hidden = this.selectedPatientIds.length === 1;
    this.showPatientTextareas();
  }

  /**
   * Called when the dialog is hidden. Clean up task can be performed here.
   */
  dialogHidden() {
    this.resetForm();
    this.removeAttendanceIds();
    this.removePatientIds();
  }

  /**
   * Update the title of the attendance dialog to indicate the attendance type.
   */
  updateDialogTitle(attendanceTypeId: string) {
    const title = document.getElementById(`${this.randomId}_title`);
    if (!title) return;

    const attendanceName = this.getAttendanceTypeNameById(attendanceTypeId);
    if (attendanceName) {
      title.textContent = `${attendanceName} reason`;
    }
  }

  /**
   * Returns the the attendance type name based on the attendance id.
   */
  getAttendanceTypeNameById(id: string): string | null {
    const attendanceType = this.attendanceTypes.find((at) => at.id === id);
    return attendanceType ? attendanceType.name : null; // Returns null if ID is not found
  }

  /**
   * Called when the "Same reason" checkbox is toggled.
   */
  toggleSameReason({ target: checkbox }: Event) {
    if (!(checkbox instanceof HTMLInputElement)) return;

    if (checkbox.checked) {
      this.hidePatientTextareas();
      this.sameReasonTextAreaActive(true);
      this.appendPatientIds(this.form);
    } else {
      this.showPatientTextareas();
      this.sameReasonTextAreaActive(false);
      this.removePatientIds();
    }
  }

  /**
   * Called when the attendance has been marked successfully.
   */
  attendanceMarked(event: CustomEvent) {
    this.dialog.hideWithoutEmitting();
    $(this.dropdown).dropdown('hide');
    this.emit('marked', { detail: event.detail[0] });
  }

  private handleDropdownShown() {
    if (this.presentForm) {
      this.appendPatientIds(this.presentForm);
    }
    this.appendPatientIds(this.clearForm);
  }

  private handleDropdownHidden() {
    this.removePatientIds();
  }

  private resetForm() {
    this.form.reset();
    this.sameReasonTextAreaActive(false);
  }

  /**
   * Shows the textarea of the patients that were selected from the checkboxes.
   */
  private showPatientTextareas() {
    for (const container of this.textareaContainers) {
      const textarea = container.querySelector('textarea');
      const { patientId } = container.dataset;
      if (!textarea || !patientId) return;

      const isSelected = this.selectedPatientIds.includes(patientId);
      container.hidden = !isSelected;
      textarea.required = isSelected;
    }
  }

  private hidePatientTextareas() {
    for (const container of this.textareaContainers) {
      const textarea = container.querySelector('textarea');
      if (!textarea) return;
      container.hidden = true;
      textarea.required = false;
    }
  }

  private sameReasonTextAreaActive(value: boolean) {
    const textarea = this.sameReasonTextareaContainer.querySelector('textarea');
    if (!textarea) return;

    this.sameReasonTextareaContainer.hidden = !value;
    textarea.required = value;
  }

  private appendPatientIds(form: HTMLFormElement) {
    for (const id of this.selectedPatientIds) {
      const hiddenInput = createHiddenInput(this.patientIdsName, id);
      form.append(hiddenInput);
    }
  }

  private removeAttendanceIds() {
    const hiddenInputs = Array.from(this.querySelectorAll(`[name="${this.attendanceTypeIdName}"]`));
    hiddenInputs.forEach((input) => input.remove());
  }

  private removePatientIds() {
    const hiddenInputs = Array.from(this.querySelectorAll(`[name="${this.patientIdsName}"]`));
    hiddenInputs.forEach((input) => input.remove());
  }
}
